import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import FormLayout from "../../../ui/layouts/FormLayout/FormLayout";
import Button, {ButtonType} from "../../../ui/components/Button/Button";
import {useTranslation} from "react-i18next";
import apiAxios from "../../../../app/api/apiAxios";
import ResultCode from "../../../../data/enums/ResultCode";
import getServerErrorResourceName from "../../../../data/apiErrors/apiErrors";
import AdminForm from "./AdminForm";
import {toast} from "react-toastify";
import {useServerError} from "../../../../hooks/useServerError";

const initialState = {
    id: 0,
    name: '',
    login: '',
    isBlocked: false,
    permission: {
        news: 0,
        newsSources: 0,
        admins: 0,
        settings: 0,
        adds: 0,
        views: 0,
        userLog: 0,
        alerts: 0,
        barriers: 0,
        services: 0,
        buildings: 0,
        accounts: 0,
        erip: 0,
        tsPols: 0,
        analytics: 0,
    }
}

const Admin = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {id} = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [admin, setAdmin] = useState(initialState);
    const [serverError, setServerError] = useServerError(null);

    useEffect(() => {
        (async () => await load(id))();
    }, [id]);

    const load = async (id) => {
        if (id && id > 0) {
            setServerError(null);
            setAdmin(initialState);
            setIsLoading(true);

            const url = `${process.env.REACT_APP_BE_DOMAIN}/api/admin/getAdmin?id=${id}`;

            try {
                const response = await apiAxios.get(url);

                if (response) {
                    if (response.status === 200 && response.data) {
                        if (response.data.resultCode === ResultCode.Success) {
                            setAdmin(response.data);
                        } else {
                            setServerError(t(getServerErrorResourceName({
                                model: 'admin',
                                resultCode: response.data.resultCode
                            })));
                        }
                    }
                }

                setIsLoading(false);
            } catch (e) {
                if (e && e.message) {
                    setServerError(`${t(getServerErrorResourceName({
                        model: 'admin',
                        resultCode: ResultCode.ServerError
                    }))} Message: (${e.message})`);
                }
                setIsLoading(false);
            }
        }
    }

    const save = async (values, actions) => {
        try {
            setServerError(null);
            setIsLoading(true);
            actions.setSubmitting(true);

            const response = await apiAxios.post(`${process.env.REACT_APP_BE_DOMAIN}/api/admin/save`, {
                id: admin.id,
                name: values.name,
                login: values.login,
                isBlocked: values.isBlocked,
                permission: {
                    news: values.news,
                    newsSources: values.newsSources,
                    admins: values.admins,
                    settings: values.settings,
                    adds: values.adds,
                    views: values.views,
                    userLog: values.userLog,
                    alerts: values.alerts,
                    barriers: values.barriers,
                    services: values.services,
                    buildings: values.buildings,
                    accounts: values.accounts,
                    erip: values.erip,
                    tsPols: values.tsPols,
                    analytics: values.analytics,
                }
            });

            if (response) {
                if (response.status === 200 && response.data) {
                    if (response.data.resultCode === ResultCode.Success) {
                        toast.success(t("admin.form.saved-label"));

                        if (response.data.description) {
                            if (!id || id <= 0) {
                                await load(+response.data.description);
                            }
                        }
                    } else {
                        setServerError(t(getServerErrorResourceName({
                            model: 'admin',
                            resultCode: response.data.resultCode,
                            description: response.data.description
                        })));
                    }
                }
            }

            setIsLoading(false);
            actions.setSubmitting(false);
        } catch (err) {
            if (err && err.message) {
                setServerError(`${t(getServerErrorResourceName({
                    model: 'admin',
                    resultCode: ResultCode.ServerError
                }))} Message: (${err.message})`);
            }
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    }

    return (
        <FormLayout header={<div className='default-form-layout-header'>{t("admin.form.admin-label")}</div>}
                    footer={(
                        <div className='default-form-layout-footer'>
                            <Button buttonType={ButtonType.Back}
                                    onClick={() => navigate(-1)}
                                    disabled={isLoading}>
                                {t("btn.back")}
                            </Button>
                            <Button buttonType={ButtonType.Default}
                                    form='admin-form'
                                    type='submit'
                                    disabled={isLoading}>
                                {t("btn.save")}
                            </Button>
                        </div>
                    )}
                    footerPosition='top'
                    isLoading={isLoading}>
            <div className='default-form-layout-container'>
                {serverError &&
                    <div className='server-error-container'>
                        <div>
                            {serverError}
                        </div>
                    </div>
                }
                <AdminForm onSubmit={save} {...admin}/>
            </div>
        </FormLayout>
    );
};

export default Admin;
